/**
 * @fileoverview Centralized Tool Applicability Configuration
 *
 * This file defines common context type groupings used by tools.
 * By centralizing these configurations, we ensure consistency across
 * all tools and make it easy to add new shape types system-wide.
 *
 * @author Claude Code
 * @version 1.0.0
 */

import { ContextType, DiagramType, PresentationOwnership } from '../interfaces/ITool';

/**
 * Style tools mount only while the USER owns presentation. Freeform domains
 * (genogram, flowchart, …) never publish 'system', so they are unaffected;
 * managed org charts publish 'system' and hand styling to lenses instead
 * (emergent-canvas-chrome plan §3–4). Annotation-only selections resolve to
 * 'user' even on managed charts — see the selection-aware ownership
 * resolution in DiagramCanvasWithTransform.
 */
const USER_OWNED_PRESENTATION: PresentationOwnership[] = ['user'];

/**
 * Standard shape types that support basic shape operations
 * (fill color, stroke, transformations, etc.)
 */
export const STANDARD_SHAPE_TYPES: ContextType[] = [
  'person',
  'genericShape',
  'sticky-note',
  'image',
];

/**
 * Shape types that support text editing
 * (font, color, alignment, styles, etc.)
 */
export const TEXT_EDITABLE_TYPES: ContextType[] = [
  'text',
  'person',
  'genericShape',
  'sticky-note'
];

/**
 * All diagram types supported by the application
 */
export const ALL_DIAGRAM_TYPES: DiagramType[] = [
  'genogram',
  'org',
  'database',
  'flowchart',
  'network',
  'uml',
  'generic'
];

/**
 * Common applicability configurations for tools
 */
export const TOOL_APPLICABILITY = {
  /**
   * For tools that work with standard shapes (fill, stroke, etc.)
   */
  STANDARD_SHAPES: {
    contextTypes: STANDARD_SHAPE_TYPES,
    diagramTypes: ALL_DIAGRAM_TYPES
  },

  /**
   * For tools that work with text (font, alignment, etc.)
   */
  TEXT_SHAPES: {
    contextTypes: TEXT_EDITABLE_TYPES,
    requires: { hasText: true },
    diagramTypes: ALL_DIAGRAM_TYPES,
    presentationModes: USER_OWNED_PRESENTATION
  },

  /**
   * For tools that work with all shape types including text
   */
  ALL_SHAPES: {
    contextTypes: [...STANDARD_SHAPE_TYPES, 'text' as ContextType],
    diagramTypes: ALL_DIAGRAM_TYPES,
    presentationModes: USER_OWNED_PRESENTATION
  },

  /**
   * For genogram-specific person tools
   */
  PERSON_ONLY: {
    contextTypes: ['person' as ContextType],
    diagramTypes: ['genogram' as DiagramType]
  },

  /**
   * For connector/edge tools
   */
  CONNECTORS: {
    contextTypes: ['connector' as ContextType],
    diagramTypes: ALL_DIAGRAM_TYPES
  }
};
